import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy, Link2, KeyRound, Trash2, ShieldCheck } from "lucide-react";
import { api, apiError, dateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell, PageHeader, Panel, EmptyState, SkeletonRows, Btn, Tag } from "@/components/Shell";

const ROLES = [
  ["manager", "Manager", "Products, POs, adjustments, unmatched lines"],
  ["warehouse", "Warehouse", "Counts, receipts, issues, uploads"],
  ["read_only", "Read-only", "View inventory, gaps and reports"],
];
const ROLE_TONE = { admin: "violet", manager: "blue", warehouse: "green", read_only: "slate" };

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "warehouse", method: "temp_password" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/team");
    setData(data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, []);

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/team/invite", form);
      setResult(data);
      toast.success(data.method === "link" ? "Invite link ready to share" : "Account created");
      setForm({ ...form, email: "", name: "" });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const update = async (member, patch) => {
    try {
      await api.put(`/team/${member.id}`, patch);
      toast.success(`${member.email} updated`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (member) => {
    if (!window.confirm(`Remove ${member.email} from this organisation?`)) return;
    try {
      await api.delete(`/team/${member.id}`);
      toast.success(`${member.email} removed`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const revoke = async (inviteRow) => {
    try {
      await api.delete(`/team/invitations/${inviteRow.id}`);
      toast.success("Invite revoked");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.info(text);
    }
  };

  const inviteUrl = (path) => `${window.location.origin}${path}`;

  return (
    <AppShell>
      <PageHeader
        title="Team"
        subtitle="Invite staff and set what they can do. Unlimited users on every plan."
        testId="team-header"
      />

      {!isAdmin && (
        <div className="mb-4 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="team-not-admin">
          Only admins can invite or change team members.
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-12">
        {isAdmin && (
          <Panel title="Invite someone" testId="team-invite-panel" className="xl:col-span-4">
            <div className="space-y-3 p-4">
              <label className="block text-sm">
                Work email
                <input data-testid="invite-email-input" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" />
              </label>
              <label className="block text-sm">
                Name
                <input data-testid="invite-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" />
              </label>
              <div className="space-y-2">
                <p className="text-sm">Role</p>
                {ROLES.map(([value, label, desc]) => (
                  <label key={value} className={`flex cursor-pointer items-start gap-2 border p-2.5 text-sm transition-colors ${form.role === value ? "border-slate-900" : "border-slate-200 hover:border-slate-400"}`}>
                    <input type="radio" name="role" data-testid={`invite-role-${value}`}
                      checked={form.role === value} onChange={() => setForm({ ...form, role: value })} className="mt-1" />
                    <span>
                      {label}
                      <span className="block text-xs text-slate-500">{desc}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="flex gap-px bg-slate-200">
                {[["temp_password", "Temporary password"], ["link", "Invite link"]].map(([value, label]) => (
                  <button key={value} data-testid={`invite-method-${value}`}
                    onClick={() => setForm({ ...form, method: value })}
                    className={`flex-1 px-3 py-2 text-xs transition-colors ${form.method === value ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:text-slate-900"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <Btn className="w-full justify-center" data-testid="invite-submit-button"
                disabled={busy || !form.email.trim() || !form.name.trim()} onClick={invite}>
                <UserPlus className="h-4 w-4" /> {busy ? "Creating…" : "Send invite"}
              </Btn>
              <p className="text-xs text-slate-500">
                No email is sent from this build — copy the password or link and pass it on. Links
                expire after 7 days.
              </p>

              {result && (
                <div className="border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="invite-result">
                  <p className="font-medium text-emerald-900">{result.email} · {result.role.replace("_", " ")}</p>
                  {result.method === "temp_password" ? (
                    <div className="mt-2 flex items-center gap-2">
                      <KeyRound className="h-4 w-4 text-emerald-700" />
                      <code className="mono bg-white px-2 py-1 text-xs" data-testid="invite-temp-password">{result.temporary_password}</code>
                      <Btn variant="outline" className="px-2 py-1 text-xs" data-testid="invite-copy-password"
                        onClick={() => copy(result.temporary_password, "Password")}>
                        <Copy className="h-3 w-3" /> Copy
                      </Btn>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center gap-2">
                      <Link2 className="h-4 w-4 text-emerald-700" />
                      <code className="mono break-all bg-white px-2 py-1 text-[11px]" data-testid="invite-link">{inviteUrl(result.invite_path)}</code>
                      <Btn variant="outline" className="px-2 py-1 text-xs" data-testid="invite-copy-link"
                        onClick={() => copy(inviteUrl(result.invite_path), "Invite link")}>
                        <Copy className="h-3 w-3" /> Copy
                      </Btn>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-emerald-800">
                    {result.method === "temp_password"
                      ? "They'll be asked to set their own password at first sign-in."
                      : "They choose their own password when they open the link."}
                  </p>
                </div>
              )}
            </div>
          </Panel>
        )}

        <Panel title="Members" testId="team-members-panel" className={isAdmin ? "xl:col-span-8" : "xl:col-span-12"}>
          {!data ? (
            <SkeletonRows rows={4} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Role</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.members.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50" data-testid={`team-member-${m.email}`}>
                      <td className="px-4 py-2">{m.name} {m.is_you && <Tag tone="slate">You</Tag>}</td>
                      <td className="px-3 py-2 text-slate-600">{m.email}</td>
                      <td className="px-3 py-2">
                        {isAdmin && !m.is_you ? (
                          <select
                            data-testid={`team-role-select-${m.email}`}
                            value={m.role}
                            onChange={(e) => update(m, { role: e.target.value })}
                            className="border border-slate-300 bg-white px-2 py-1 text-xs"
                          >
                            {["admin", ...ROLES.map(([v]) => v)].map((r) => (
                              <option key={r} value={r}>{r.replace("_", " ")}</option>
                            ))}
                          </select>
                        ) : (
                          <Tag tone={ROLE_TONE[m.role]}>{m.role.replace("_", " ")}</Tag>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {m.active === false ? <Tag tone="red">Deactivated</Tag> : <Tag tone="green">Active</Tag>}
                        {m.must_change_password && <Tag tone="amber">Temp password</Tag>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isAdmin && !m.is_you && (
                          <div className="flex justify-end gap-1">
                            <Btn variant="outline" className="px-2 py-1 text-xs"
                              data-testid={`team-reset-password-${m.email}`}
                              onClick={async () => {
                                try {
                                  const { data } = await api.post(`/team/${m.id}/reset-temp-password`);
                                  setResult({ method: "temp_password", email: data.email, role: m.role, temporary_password: data.temporary_password });
                                  toast.success("New temporary password issued");
                                  await load();
                                } catch (e) {
                                  toast.error(apiError(e));
                                }
                              }}>
                              Reset password
                            </Btn>
                            <Btn variant="outline" className="px-2 py-1 text-xs"
                              data-testid={`team-toggle-active-${m.email}`}
                              onClick={() => update(m, { active: m.active === false })}>
                              {m.active === false ? "Reactivate" : "Deactivate"}
                            </Btn>
                            <Btn variant="danger" className="px-2 py-1 text-xs"
                              data-testid={`team-remove-${m.email}`} onClick={() => remove(m)}>
                              <Trash2 className="h-3 w-3" />
                            </Btn>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Pending invites" testId="team-invites-panel" className="xl:col-span-12">
          {!data ? (
            <SkeletonRows rows={2} />
          ) : data.invitations.length === 0 ? (
            <EmptyState icon={ShieldCheck} title="No pending invites" body="Everyone invited has an account." testId="team-invites-empty" />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Expires</th>
                  <th className="px-3 py-2 text-left">Invited by</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.invitations.map((i) => (
                  <tr key={i.id} data-testid={`team-invite-${i.email}`}>
                    <td className="px-4 py-2">{i.email}</td>
                    <td className="px-3 py-2"><Tag tone={ROLE_TONE[i.role]}>{i.role.replace("_", " ")}</Tag></td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {dateTime(i.expires_at)} {i.expired && <Tag tone="red">Expired</Tag>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{i.invited_by}</td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <div className="flex justify-end gap-1">
                          <Btn variant="outline" className="px-2 py-1 text-xs"
                            data-testid={`invite-copy-${i.email}`}
                            onClick={() => copy(inviteUrl(i.invite_path), "Invite link")}>
                            <Copy className="h-3 w-3" /> Link
                          </Btn>
                          <Btn variant="danger" className="px-2 py-1 text-xs"
                            data-testid={`invite-revoke-${i.email}`} onClick={() => revoke(i)}>
                            Revoke
                          </Btn>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>
    </AppShell>
  );
}
